import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, shareReplay, take } from 'rxjs/operators';

import { RootState } from 'src/app/reducers';
import {
  BOOKMARK_RESULTS_PER_PAGE,
  BookmarkSortType,
  BookmarkSortTypeDisplayName,
  PAGINATION_WINDOW_SIZE,
  SEARCH_RESULTS_PER_PAGE,
  SUBJECT_NAMES,
  SubjectId
} from 'src/app/resources/config';
import { Log } from 'src/app/utils/log';
import { navigate, openWindow, setBrowserTitle, setTitle } from 'src/app/actions/core.actions';
import {
  ReadableEnglishProblem,
  ReadableHistoryProblem,
  ReadableNationalLanguageProblem,
  ReadableScienceProblem
} from 'src/app/models/problem';
import { enter, fadeInOut } from 'src/app/resources/animations';
import { Bookmark } from 'src/app/models/bookmark';
import {
  getBookmarkCount,
  getBookmarkSearching,
  getReadableEnglishBookmarkProblems,
  getReadableMathBookmarkProblems,
  getReadableNationalLanguageBookmarkProblems,
  getReadablePhysicsBookmarkProblems,
  getReadableChemistryBookmarkProblems,
  getReadableBiologyBookmarkProblems,
  getReadableJapaneseHistoryBookmarkProblems,
  getReadableWorldHistoryBookmarkProblems,
  getReadableGeographyBookmarkProblems,
  getReadablePoliticalEconomyBookmarkProblems,
  getReadableSpecialAdmissionsBookmarkProblems
} from 'src/app/selectors/bookmark.selectors';
import * as BookmarkActions from 'src/app/actions/bookmark.actions';
import {
  findBookmarkCount,
  initializeFindBookmarkCountState,
  initializeFindBookmarkProblemsState,
  findBookmarkProblems
} from 'src/app/actions/bookmark.actions';
import { getSignedInUser } from 'src/app/selectors/auth.selectors';
import { User } from 'src/app/models/user';
import { SearchBookmarkCondition } from '../../../models/search-bookmark-condition';
import { QueryParamsMapper } from '../../../mappers/query-params-mapper';
import { RoutingPathResolver } from '../../../app-routing-path-resolver';

interface SelectableSortType {
  type: BookmarkSortType;
  displayName: string;
}

@Component({
  selector: 'app-bookmark-problem-lists-unlisted',
  templateUrl: './bookmark-problem-lists-unlisted.component.html',
  styleUrls: ['./bookmark-problem-lists-unlisted.component.scss'],
  animations: [enter, fadeInOut]
})
export class BookmarkProblemListsUnlistedComponent implements OnInit, OnDestroy {
  @ViewChild('pageTop') pageTop: ElementRef;
  readableEnglishProblems$: Observable<ReadableEnglishProblem[]>;
  readableMathProblems$: Observable<ReadableScienceProblem[]>;
  readableNationalLanguageProblems$: Observable<ReadableNationalLanguageProblem[]>;
  readablePhysicsProblems$: Observable<ReadableScienceProblem[]>;
  readableChemistryProblems$: Observable<ReadableScienceProblem[]>;
  readableBiologyProblems$: Observable<ReadableScienceProblem[]>;
  readableJapaneseHistoryProblems$: Observable<ReadableHistoryProblem[]>;
  readableWorldHistoryProblems$: Observable<ReadableHistoryProblem[]>;
  readableGeographyProblems$: Observable<ReadableScienceProblem[]>;
  readablePoliticalEconomyProblems$: Observable<ReadableScienceProblem[]>;
  readableSpecialAdmissionsProblems$: Observable<ReadableScienceProblem[]>;
  bookmarkSearching$: Observable<boolean>;

  ENGLISH_SUBJECT_ID: string;
  MATH_SUBJECT_ID: string;
  NATIONAL_LANGUAGE_SUBJECT_ID: string;
  PHYSICS_SUBJECT_ID: string;
  CHEMISTRY_SUBJECT_ID: string;
  BIOLOGY_SUBJECT_ID: string;
  JAPANESE_HISTORY_SUBJECT_ID: string;
  WORLD_HISTORY_SUBJECT_ID: string;
  GEOGRAPHY_SUBJECT_ID: string;
  POLITICAL_ECONOMY_SUBJECT_ID: string;
  SPECIAL_ADMISSIONS_SUBJECT_ID: string;

  user: User;
  totalBookmarkCount: number;
  selectableSortTypes: SelectableSortType[];
  selectedSortType: SelectableSortType;
  // 表示用
  bookmark: Bookmark;
  initializedBookmark$ = new BehaviorSubject(true);
  bookmarkedAllFlag = true;
  // pagination ----------
  currentPage = 1;
  totalPage: number;
  paginationPageSize: number;
  paginationWindowSize: number;
  private LOG_SOURCE = this.constructor.name;
  private title = '未分類のリスト';
  private firstPageInitializedSubject = new BehaviorSubject(false);
  firstPageInitialized$ = this.firstPageInitializedSubject.asObservable();

  subjectId: string; // クエリパラメタより取得した科目ID
  subjectName: string;
  signedInUser$: Observable<User>;

  protected readonly SubjectId = SubjectId;

  constructor(private store: Store<RootState>, private activatedRoute: ActivatedRoute, private location: Location) {
    this.ENGLISH_SUBJECT_ID = SubjectId.ENGLISH;
    this.MATH_SUBJECT_ID = SubjectId.MATH;
    this.NATIONAL_LANGUAGE_SUBJECT_ID = SubjectId.NATIONAL_LANGUAGE;
    this.PHYSICS_SUBJECT_ID = SubjectId.PHYSICS;
    this.CHEMISTRY_SUBJECT_ID = SubjectId.CHEMISTRY;
    this.BIOLOGY_SUBJECT_ID = SubjectId.BIOLOGY;
    this.JAPANESE_HISTORY_SUBJECT_ID = SubjectId.JAPANESE_HISTORY;
    this.WORLD_HISTORY_SUBJECT_ID = SubjectId.WORLD_HISTORY;
    this.GEOGRAPHY_SUBJECT_ID = SubjectId.GEOGRAPHY;
    this.POLITICAL_ECONOMY_SUBJECT_ID = SubjectId.POLITICAL_ECONOMY;
    this.SPECIAL_ADMISSIONS_SUBJECT_ID = SubjectId.SPECIAL_ADMISSIONS;

    this.paginationPageSize = SEARCH_RESULTS_PER_PAGE;
    this.paginationWindowSize = PAGINATION_WINDOW_SIZE;
    this.setUpSelectableSortTypes();
  }

  ngOnInit() {
    this.store.dispatch(setBrowserTitle({ subTitle: this.title }));
    setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));

    this.setUpUser();
    this.setUpSubject();

    this.bookmarkSearching$ = this.store.select(getBookmarkSearching);
    if (this.subjectId === SubjectId.ENGLISH) this.setUpEnglishBookmarkProblems();
    if (this.subjectId === SubjectId.MATH) this.setUpMathBookmarkProblems();
    if (this.subjectId === SubjectId.NATIONAL_LANGUAGE) this.setUpNationalLanguageBookmarkProblems();
    if (this.subjectId === SubjectId.PHYSICS) this.setUpPhysicsBookmarkProblems();
    if (this.subjectId === SubjectId.CHEMISTRY) this.setUpChemistryBookmarkProblems();
    if (this.subjectId === SubjectId.BIOLOGY) this.setUpBiologyBookmarkProblems();
    if (this.subjectId === SubjectId.JAPANESE_HISTORY) this.setUpJapaneseHistoryBookmarkProblems();
    if (this.subjectId === SubjectId.WORLD_HISTORY) this.setUpWorldHistoryBookmarkProblems();
    if (this.subjectId === SubjectId.GEOGRAPHY) this.setUpGeographyBookmarkProblems();
    if (this.subjectId === SubjectId.POLITICAL_ECONOMY) this.setUpPoliticalEconomyBookmarkProblems();
    if (this.subjectId === SubjectId.SPECIAL_ADMISSIONS) this.setUpSpecialAdmissionsBookmarkProblems();

    this.findFirstPageProblems();
  }

  ngOnDestroy() {
    this.store.dispatch(BookmarkActions.initializeFindBookmarkState());
    this.store.dispatch(BookmarkActions.initializeFindBookmarkProblemsState());
    this.store.dispatch(initializeFindBookmarkCountState());
  }

  goBack() {
    this.location.back();
  }

  selectPage(page: number) {
    Log.debug(this.LOG_SOURCE, `選択された page: ${page} 並び順: ${this.selectedSortType.type}`);
    this.scrollToTop();
    this.searchBookmarkProblems(page, this.selectedSortType.type);
    this.currentPage = page;
  }

  onChangeSubject() {
    this.currentPage = 1;
    this.findFirstPageProblems();
  }

  onChangeSortType() {
    this.currentPage = 1;
    this.findFirstPageProblems();
  }

  showProblemDetail(problemObj: { problemId: string }) {
    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([user]) => {
        const condition: SearchBookmarkCondition = {
          userId: user.id,
          subjectId: this.subjectId,
          sortType: this.selectedSortType.type
        };

        const url = RoutingPathResolver.resolveProblemDetail(problemObj.problemId);
        const queryParams = QueryParamsMapper.encodeBookmarkSearchProblemsCondition(condition);
        Log.debug(this.LOG_SOURCE, 'お気に入り大問詳細画面用の query params: ', queryParams);
        this.store.dispatch(openWindow({ url, extras: { queryParams: { ...queryParams, from: 'bookmark' } } }));
      });
  }

  // set ups ----------------------------------------------------------------------------------------------
  private setUpUser() {
    this.signedInUser$ = this.store.select(getSignedInUser).pipe(filter<User>(it => it != null));
  }

  private setUpSubject() {
    this.subjectId = this.activatedRoute.snapshot.paramMap.get('subjectId') ?? '';

    this.signedInUser$.pipe(take(1)).subscribe(signedInUser => {
      if (!signedInUser.visibleSubjectIds || !signedInUser.visibleSubjectIds.includes(this.subjectId as SubjectId)) {
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveBookmarkProblemLists() }));
        return;
      }
      this.subjectName = SUBJECT_NAMES[this.subjectId as SubjectId];
    });
  }

  private setUpEnglishBookmarkProblems() {
    this.readableEnglishProblems$ = this.store.select(getReadableEnglishBookmarkProblems).pipe(shareReplay(1));
    this.readableEnglishProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpMathBookmarkProblems() {
    this.readableMathProblems$ = this.store.select(getReadableMathBookmarkProblems).pipe(shareReplay(1));
    this.readableMathProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpNationalLanguageBookmarkProblems() {
    this.readableNationalLanguageProblems$ = this.store.select(getReadableNationalLanguageBookmarkProblems).pipe(shareReplay(1));
    this.readableNationalLanguageProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpPhysicsBookmarkProblems() {
    this.readablePhysicsProblems$ = this.store.select(getReadablePhysicsBookmarkProblems).pipe(shareReplay(1));
    this.readablePhysicsProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpChemistryBookmarkProblems() {
    this.readableChemistryProblems$ = this.store.select(getReadableChemistryBookmarkProblems).pipe(shareReplay(1));
    this.readableChemistryProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpBiologyBookmarkProblems() {
    this.readableBiologyProblems$ = this.store.select(getReadableBiologyBookmarkProblems).pipe(shareReplay(1));
    this.readableBiologyProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpJapaneseHistoryBookmarkProblems() {
    this.readableJapaneseHistoryProblems$ = this.store.select(getReadableJapaneseHistoryBookmarkProblems).pipe(shareReplay(1));
    this.readableJapaneseHistoryProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpWorldHistoryBookmarkProblems() {
    this.readableWorldHistoryProblems$ = this.store.select(getReadableWorldHistoryBookmarkProblems).pipe(shareReplay(1));
    this.readableWorldHistoryProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpGeographyBookmarkProblems() {
    this.readableGeographyProblems$ = this.store.select(getReadableGeographyBookmarkProblems).pipe(shareReplay(1));
    this.readableGeographyProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpPoliticalEconomyBookmarkProblems() {
    this.readablePoliticalEconomyProblems$ = this.store.select(getReadablePoliticalEconomyBookmarkProblems).pipe(shareReplay(1));
    this.readablePoliticalEconomyProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpSpecialAdmissionsBookmarkProblems() {
    this.readableSpecialAdmissionsProblems$ = this.store.select(getReadableSpecialAdmissionsBookmarkProblems).pipe(shareReplay(1));
    this.readableSpecialAdmissionsProblems$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(() => {
        this.firstPageInitializedSubject.next(true);
      });
  }

  private setUpTotalPage(totalBookmarkCount: number) {
    this.totalPage = Math.ceil(totalBookmarkCount / BOOKMARK_RESULTS_PER_PAGE);
  }

  private setUpSelectableSortTypes() {
    const sortTypeDefault: SelectableSortType = {
      type: BookmarkSortType.CREATED_AT_DESC,
      displayName: BookmarkSortTypeDisplayName.CREATED_AT_DESC
    };
    this.selectableSortTypes = [
      sortTypeDefault,
      { type: BookmarkSortType.CREATED_AT_ASC, displayName: BookmarkSortTypeDisplayName.CREATED_AT_ASC }
    ];
    this.selectedSortType = sortTypeDefault;
  }

  private findFirstPageProblems() {
    this.store.dispatch(initializeFindBookmarkCountState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([user]) => {
        this.store.dispatch(findBookmarkCount({ userId: user.id, subjectId: this.subjectId }));
        combineLatest([
          this.store.select(getBookmarkCount).pipe(
            filter(it => it != null),
            take(1)
          )
        ]).subscribe(([bookmarkCount]) => {
          Log.debug(this.LOG_SOURCE, 'bookmarkCount', bookmarkCount);
          this.totalBookmarkCount = bookmarkCount;

          if (bookmarkCount === 0) {
            Log.warn(this.LOG_SOURCE, `条件に該当する大問が 0 件です`);
            this.firstPageInitializedSubject.next(true);
            return;
          }

          this.setUpTotalPage(bookmarkCount);
          this.selectPage(this.currentPage);
        });
      });
  }

  private searchBookmarkProblems(page: number, sortType: BookmarkSortType) {
    this.store.dispatch(initializeFindBookmarkProblemsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([user]) => {
        if (!Object.values(SubjectId).includes(this.subjectId as SubjectId)) {
          Log.error(this.LOG_SOURCE, 'subjectId が想定されていないものです: ', this.subjectId);
          return;
        }

        if (!user || !user.id) {
          Log.error(this.LOG_SOURCE, 'signedInUser が取得できませんでした');
          return;
        }

        this.findBookmarkProblems(this.subjectId as SubjectId, page, sortType, user.id);
      });
  }

  private findBookmarkProblems(subjectId: SubjectId, page: number, sortType: BookmarkSortType, userId: string) {
    const subjectName = SUBJECT_NAMES[subjectId];
    Log.debug(this.LOG_SOURCE, `${page} ページ目の ${subjectName} の大問を取得します`);

    const condition: SearchBookmarkCondition = {
      userId,
      subjectId,
      sortType,
      page
    };
    this.store.dispatch(findBookmarkProblems({ condition }));
  }

  private scrollToTop() {
    this.pageTop.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
